import React from 'react';

import { SettingsCoordinator } from '~settings-coordinator/SettingsCoordinator';
import { SettingsFormikContainer } from '~src/settings/components/common/SettingsFormikContainer';
import { normalizeSectionSpec } from '~src/settings/components/settings-coordinator/SettingsSectionList';
import {FeatureFlags} from '~src/ts/FeatureFlags';

import { ExtensionsSettingsSubspaceState } from '../../../../app/spaces/SharedSettingsSpace/ExtensionsSettingsSubspaceShared';
import { getSessionManagementServiceInstance } from '../../../../sessionManagement/SessionManagementService';
import { coordinatorSubmitFailedHandler, coordinatorSubmitHandler } from '../common/GeneralToastHandlers';

export type ExtensionsSettingsSubFormikProps = {
  settings: ExtensionsSettingsSubspaceState;
}

export type AnalyticsExtensionsSettingsSubFormikProps = {
  settings: ExtensionsSettingsSubspaceState;
}

export type PrepExtensionsSettingsSubFormikProps = {
  settings: ExtensionsSettingsSubspaceState;
}


/**
 * Creates a settings coordinator with the site settings for site admin and site settings for server admin forms
 */
export function ExtensionsSettingsCoordinator(props: ExtensionsSettingsSubspaceState) {
  // sections here are dynamic, with some dynamic layout happening at
  //  the top-level sections, and some layout happening inside the
  //  sections through passed in props (as formik settings)
  let sections = Array<string>();
  const sessionManagementService = getSessionManagementServiceInstance();
  const isSingleSiteOnPremServer = sessionManagementService.isSingleSiteOnPremServer();
  const isDashboardExtensionsEnabled = sessionManagementService.isDashboardExtensionsEnabled();

  if (props.isServerPage) {
    if (isDashboardExtensionsEnabled) {
      sections.push('ExtensionsServerSection');

      if (!props.isMultiSite) {
        sections.push('ExtensionsSiteSection');
      }
    }

    if (isSingleSiteOnPremServer) {
      sections.push('AnalyticsExtensionsSiteSection');
    } else {
      sections.push('AnalyticsExtensionsServerSection');
    }
  } else {
    if (isDashboardExtensionsEnabled) {
      sections.push('ExtensionsSiteSection');
    }

    sections.push('AnalyticsExtensionsSiteSection');
  }
  if (FeatureFlags.prepExtensionsEinstein) {
    sections.push('PrepExtensionsSiteSection');
  }

  return (
    <SettingsCoordinator
      onSubmit={coordinatorSubmitHandler}
      onSubmitFailure={coordinatorSubmitFailedHandler}>

        <SettingsFormikContainer
              formName={'ExtensionsSettings'}
              settings={props}
              sections={sections}
              onSubmit={useSubmitHandler(props, sections)}
        />

    </SettingsCoordinator>
  );
}

function useSubmitHandler(settings: ExtensionsSettingsSubspaceState, sections: string[]) {
  return React.useCallback(
    async (settings: ExtensionsSettingsSubspaceState) => {
      const perSectionSubmitPromises = sections
        ?.map(normalizeSectionSpec)
        ?.map(([Section]) => Section.onSubmit?.(settings))
        ?.filter(result => !!result);

      await Promise.all([
        ...perSectionSubmitPromises
      ]);
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settings]
  );
}
